import { partition } from 'lodash'
import { ReactNode, useMemo } from 'react'

import { Dayjs, dayjsEn } from '@bemlo/date'
import { getRuleViolationsMap } from '@bemlo/schedule'
import { Maybe } from '@bemlo/utils'

import { HoverCard } from '../../../../elements'
import { useTranslations } from '../../../../translate'
import { byCellFilter } from '../by-filter'
import { AvailabilityCell, Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { DraftShifts } from '../components/DraftShifts'
import { NewShift } from '../components/NewShift'
import { ResourceTitle } from '../components/ResourceTitle'
import { Row } from '../components/Row'
import { ShiftError } from '../components/ShiftError'
import { ScheduleSchedulingRules, WithResourceHoverCard } from '../types'
import { useEditShifts } from '../useEditShifts'
import { DraftShiftInput, useSchedule } from '../useSchedule'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

const CANDIDATE_STATUSES = new Set(['BOOKED', 'SUGGESTED', 'DRAFT'])

type ResourceProps<T extends object = object> = WithResourceHoverCard<T> & {
  candidateId: string
  scheduleResourceId?: string
  name: string
  avatarUrl?: Maybe<string>
  resourceUrl?: Maybe<string>
  noName?: boolean
  permanent?: boolean
  showUnitName?: boolean
  titleComponent?: ReactNode
  schedulingRules?: ScheduleSchedulingRules
  requiredDays?: number[]
  draftShiftInput?: Omit<DraftShiftInput, 'date' | 'scheduleSlotId'> | undefined
  onClickNewShiftType?: () => void
  contract?: Maybe<{
    startsAt: number | null
    endsAt: number | null
  }>
}

export const Resource = <T extends object>(props: ResourceProps<T>) => {
  const t = useTranslations()

  const {
    days,
    currentWeek,
    shifts: allShifts,
    availability: allAvailability,
    shiftPopover,
    isScrolled,
  } = useSchedule()

  const { enableEditing, onCreateDraftShift, onDeleteDraftShift } =
    useEditShifts(props.draftShiftInput)

  const { candidateShifts, candidateAvailability, dayErrors, isEmployed } =
    useMemo(() => {
      const candidateShifts = allShifts.filter(
        byCellFilter({
          scheduleResourceId: props.scheduleResourceId,
          bookedCandidateId: props.candidateId,
        }),
      )
      const candidateAvailability = allAvailability.filter(
        ({ candidateId }) => candidateId === props.candidateId,
      )
      const activeCandidateShifts = candidateShifts.filter(
        (shift) => shift.status !== 'CANCELLED',
      )

      const dayErrors = props.schedulingRules
        ? getRuleViolationsMap(
            props.schedulingRules.ruleSets,
            activeCandidateShifts,
            props.schedulingRules.rules,
            props.schedulingRules.resource,
          )
        : {}

      const unixContract = props.contract
      const contract = {
        startsAt: unixContract?.startsAt
          ? dayjsEn.unix(unixContract.startsAt)
          : null,
        endsAt: unixContract?.endsAt ? dayjsEn.unix(unixContract.endsAt) : null,
      }

      // Function to see if the shifts is within the contract
      const isEmployed = (date: Dayjs) => {
        if (!contract.startsAt || !contract.endsAt) return true
        if (contract.startsAt && date.isBefore(contract.startsAt)) return false
        if (contract.endsAt && date.isAfter(contract.endsAt)) return false
        return true
      }

      return {
        candidateShifts,
        candidateAvailability,
        dayErrors,
        isEmployed,
      }
    }, [
      allShifts,
      props.candidateId,
      props.scheduleResourceId,
      props.schedulingRules,
    ])

  const shiftsInWeek = currentWeek
    ? getShiftsInWeek(currentWeek, candidateShifts)
    : []

  if (
    !shiftsInWeek.length &&
    !candidateAvailability.length &&
    !props.permanent
  ) {
    return null
  }

  const bookedShifts = shiftsInWeek.filter(
    ({ status, bookedCandidateId }) =>
      bookedCandidateId === props.candidateId && status === 'BOOKED',
  )

  const title = props.titleComponent ?? (
    <ResourceTitle
      bold
      title={props.noName ? null : props.name}
      avatar={props.noName ? null : { url: props.avatarUrl }}
      url={props.resourceUrl}
      shifts={bookedShifts}
    />
  )

  const HoverCardContent = props.hoverCard

  return (
    <Row>
      <Cell isTitle rowSpan={1} rightShadow={isScrolled}>
        {HoverCardContent ? (
          <HoverCard
            side="right"
            trigger={<HoverCard.Trigger>{title}</HoverCard.Trigger>}
          >
            <HoverCardContent
              candidateId={props.candidateId}
              name={props.name}
              shifts={candidateShifts}
              availability={candidateAvailability}
              {...(props.hoverCardProps || ({} as T))}
            />
          </HoverCard>
        ) : (
          title
        )}
      </Cell>
      {days.map((day, i) => {
        const [draftShifts, shiftsOnDay] = partition(
          candidateShifts.filter((shift) => shift.date === day.dateISO),
          ({ status }) => status === 'DRAFT',
        )
        const isOccupied = shiftsOnDay.some(
          (shift) =>
            CANDIDATE_STATUSES.has(shift.status) && shift.bookedCandidateId,
        )
        const availabilityOnDay = candidateAvailability.filter(
          ({ date }) => date === day.dateISO,
        )
        const errorsOnDay =
          dayErrors[day.dateISO]?.map((error) => ({
            error: t.$schedulingRuleViolation(error),
            isWarning: error.severity === 'warning',
          })) ?? []
        const isRequired = props.requiredDays?.includes(i)

        return (
          <AvailabilityCell
            key={day.dateISO}
            disabled={!day.withinRange || !isEmployed(day.date)}
            weekend={day.isWeekend}
            availability={availabilityOnDay}
            isOccupied={isOccupied}
            isRequired={isRequired}
          >
            <CellShiftsColumn>
              {shiftsOnDay.map((shift) => {
                if (shift.status === 'DRAFT') return null

                return (
                  <NewShift
                    {...props}
                    key={shift.id}
                    shift={shift}
                    status={shift.status}
                    position="center"
                    shiftIds={[shift.id]}
                    isAbsent={shift.isAbsent}
                    isAcknowledged={shift.isAcknowledged}
                    popoverContent={shiftPopover}
                    unitName={props.showUnitName ? shift.unitName : undefined}
                    error={<ShiftError errors={errorsOnDay} />}
                  />
                )
              })}
              {enableEditing && day.withinRange && (
                <DraftShifts
                  showBadges
                  shifts={draftShifts}
                  onClickNewShift={
                    props.draftShiftInput
                      ? () =>
                          onCreateDraftShift(
                            day.dateISO,
                            // TODO(BEMLO-3598)
                            '',
                          )
                      : props.onClickNewShiftType
                  }
                  onDeleteDraftShift={onDeleteDraftShift}
                />
              )}
            </CellShiftsColumn>
          </AvailabilityCell>
        )
      })}
    </Row>
  )
}
